/**
 * FailureRecurrenceGraph.jsx — FAILURE HISTORY GRAPH
 *
 * Styled to mirror the locked SignatureProofGraph (trapezoid bars with seeded
 * wobble, pressure-varied diagonal hatch, Permanent Marker percentages with
 * angular wobble, drifting hand-drawn baseline).
 *
 * Differences from the locked primitive:
 *  - Theme-aware colors (reads from --tl-text-rgb so it works in light AND dark)
 *  - Year + root-cause captions below the baseline (not a sector + proof label)
 *  - Same geometric signature; no inline deviations
 */
import React, { useMemo, useRef } from 'react';
import { motion, useInView } from 'framer-motion';

/* ── Seeded pseudo-random for deterministic wobble ── */
function seededRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

const ERAS = [
  { year: '1929', lift: 23, label: 'leverage',        height: 150, seed: 101 },
  { year: '2001', lift: 18, label: 'opacity',         height: 120, seed: 202 },
  { year: '2008', lift: 31, label: 'no verification', height: 188, seed: 303 },
  { year: '2022', lift: 15, label: 'false trust',     height: 100, seed: 404 },
];

const BASELINE_Y = 320;
const BAR_WIDTH = 78;
const BAR_GAP = 120;
const START_X = 60;

/* ── Trapezoid wobble bar path (mirror of locked primitive) ── */
function buildSketchBar(x, barHeight, seed) {
  const r = seededRandom(seed);
  const topY = BASELINE_Y - barHeight;

  const topLeftLean = (r() - 0.5) * 6;
  const topRightLean = (r() - 0.5) * 5;
  const bottomLeftDrift = (r() - 0.5) * 3;
  const bottomRightDrift = (r() - 0.5) * 3;
  const topWobble1 = (r() - 0.5) * 4;
  const topWobble2 = (r() - 0.5) * 4;
  const widthVar = (r() - 0.5) * 6;

  const bl = { x: x + bottomLeftDrift, y: BASELINE_Y + (r() - 0.5) * 2 };
  const tl = { x: x + topLeftLean + 2, y: topY + (r() - 0.5) * 3 };
  const tr = { x: x + BAR_WIDTH + topRightLean + widthVar - 2, y: topY + topWobble1 };
  const br = { x: x + BAR_WIDTH + bottomRightDrift + widthVar, y: BASELINE_Y + (r() - 0.5) * 2 };

  const leftMidY = (bl.y + tl.y) / 2;
  const rightMidY = (br.y + tr.y) / 2;
  const topMidX = (tl.x + tr.x) / 2;
  const leftWobble = (r() - 0.5) * 5;
  const rightWobble = (r() - 0.5) * 5;

  return `
    M ${bl.x} ${bl.y}
    C ${bl.x + leftWobble} ${leftMidY + 20}, ${tl.x - leftWobble * 0.7} ${leftMidY - 20}, ${tl.x} ${tl.y}
    C ${tl.x + 8} ${tl.y + topWobble2}, ${topMidX - 5} ${tl.y - topWobble1 * 0.5}, ${topMidX} ${(tl.y + tr.y) / 2 + topWobble2 * 0.3}
    C ${topMidX + 5} ${tr.y + topWobble1 * 0.4}, ${tr.x - 8} ${tr.y - topWobble2 * 0.6}, ${tr.x} ${tr.y}
    C ${tr.x + rightWobble * 0.7} ${rightMidY - 20}, ${br.x - rightWobble} ${rightMidY + 20}, ${br.x} ${br.y}
    Z
  `;
}

/* ── Pressure-varied diagonal hatch lines ── */
function buildHatchLines(x, barHeight, seed) {
  const r = seededRandom(seed + 500);
  const topY = BASELINE_Y - barHeight;
  const lines = [];
  const baseSpacing = 7;

  let offset = -BAR_WIDTH * 0.3;
  while (offset < barHeight + BAR_WIDTH * 1.3) {
    const spacing = baseSpacing + (r() - 0.5) * 3;
    offset += spacing;
    const angleVar = (r() - 0.5) * 4;
    const startX = x - 5 + (r() - 0.5) * 2;
    const startY = topY + offset;
    const endX = startX + BAR_WIDTH + 10 + (r() - 0.5) * 3;
    const endY = startY - BAR_WIDTH - 10 + angleVar;
    const opacity = 0.12 + r() * 0.18;
    const sw = 1.2 + r() * 1.2;
    const overrun = r() * 3;

    lines.push({
      x1: startX - overrun,
      y1: startY + (r() - 0.5) * 1.5,
      x2: endX + overrun,
      y2: endY + (r() - 0.5) * 1.5,
      opacity,
      sw,
    });
  }
  return lines;
}

/* ── Drifting hand-drawn baseline ── */
function buildBaselinePath(startX, endX) {
  const r = seededRandom(999);
  const segments = 12;
  const step = (endX - startX) / segments;
  let d = `M ${startX} ${BASELINE_Y + (r() - 0.5) * 2}`;
  for (let i = 1; i <= segments; i++) {
    const px = startX + i * step;
    const py = BASELINE_Y + (r() - 0.5) * 2.5;
    const cpx = startX + (i - 0.5) * step + (r() - 0.5) * 4;
    const cpy = BASELINE_Y + (r() - 0.5) * 3;
    d += ` Q ${cpx} ${cpy}, ${px} ${py}`;
  }
  return d;
}

/* ── Single hand-drawn era bar ── */
const HandDrawnEra = ({ era, x, index, delay, inView, strokeRGB }) => {
  const barPath = useMemo(() => buildSketchBar(x, era.height, era.seed), [x, era.height, era.seed]);
  const hatchLines = useMemo(() => buildHatchLines(x, era.height, era.seed), [x, era.height, era.seed]);
  const topY = BASELINE_Y - era.height;

  const r = seededRandom(era.seed + 700);
  const pctRotation = (r() - 0.5) * 5;
  const pctXDrift = (r() - 0.5) * 6;
  const pctYDrift = (r() - 0.5) * 4;
  const clipId = `era-clip-${index}`;

  return (
    <g data-testid={`failure-era-${era.year}`}>
      <defs>
        <clipPath id={clipId}>
          <path d={barPath} />
        </clipPath>
      </defs>

      {/* Bar outline — theme-aware hand-drawn stroke */}
      <motion.path
        d={barPath}
        fill="none"
        stroke={`rgba(${strokeRGB}, 0.82)`}
        strokeWidth="2.6"
        strokeLinejoin="round"
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={inView ? { pathLength: 1, opacity: 1 } : {}}
        transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      />

      {/* Hatch fill — clipped, with pressure variance */}
      <g clipPath={`url(#${clipId})`}>
        {hatchLines.map((line, i) => (
          <motion.line
            key={i}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke={`rgba(${strokeRGB}, ${line.opacity})`}
            strokeWidth={line.sw}
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={inView ? { pathLength: 1 } : {}}
            transition={{ duration: 0.4, delay: delay + 0.3 + i * 0.015 }}
          />
        ))}
      </g>

      {/* +lift% — Permanent Marker with human wobble */}
      <motion.text
        x={x + BAR_WIDTH / 2 + pctXDrift}
        y={topY - 18 + pctYDrift}
        textAnchor="middle"
        fill={`rgba(${strokeRGB}, 0.95)`}
        fontSize="26"
        fontWeight="700"
        fontFamily="'Permanent Marker', cursive"
        transform={`rotate(${pctRotation}, ${x + BAR_WIDTH / 2 + pctXDrift}, ${topY - 18 + pctYDrift})`}
        initial={{ opacity: 0, y: 12 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.5, delay: delay + 0.6 }}
      >
        +{era.lift}%
      </motion.text>

      {/* Year below baseline */}
      <motion.text
        x={x + BAR_WIDTH / 2}
        y={BASELINE_Y + 28}
        textAnchor="middle"
        fill={`rgba(${strokeRGB}, 0.7)`}
        fontSize="13"
        fontWeight="600"
        letterSpacing="0.06em"
        fontFamily="'Inter Tight', sans-serif"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.4, delay: delay + 0.7 }}
      >
        {era.year}
      </motion.text>

      {/* Root-cause label below year */}
      <motion.text
        x={x + BAR_WIDTH / 2}
        y={BASELINE_Y + 46}
        textAnchor="middle"
        fill={`rgba(${strokeRGB}, 0.35)`}
        fontSize="10"
        fontFamily="monospace"
        letterSpacing="0.04em"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.3, delay: delay + 0.8 }}
      >
        {era.label}
      </motion.text>

      {/* Faint dashed guide below label */}
      <motion.line
        x1={x + BAR_WIDTH / 2}
        y1={BASELINE_Y + 52}
        x2={x + BAR_WIDTH / 2}
        y2={BASELINE_Y + 66}
        stroke={`rgba(${strokeRGB}, 0.12)`}
        strokeWidth="1"
        strokeDasharray="2 3"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.3, delay: delay + 0.9 }}
      />
    </g>
  );
};

export default function FailureRecurrenceGraph() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const totalWidth = START_X * 2 + (ERAS.length - 1) * BAR_GAP + BAR_WIDTH;
  const baselinePath = useMemo(
    () => buildBaselinePath(START_X - 30, START_X + (ERAS.length - 1) * BAR_GAP + BAR_WIDTH + 30),
    []
  );

  // Theme-aware stroke: reads the timeline's text RGB var; fallback to neutral charcoal.
  // var(--tl-text-rgb) lives on the HistoricalTimeline wrapper and flips between light/dark.
  const strokeRGB = 'var(--tl-text-rgb, 15, 23, 42)';

  return (
    <div ref={ref} className="w-full flex justify-center" data-testid="failure-recurrence-graph">
      <svg
        viewBox={`0 0 ${totalWidth} 400`}
        className="w-full max-w-[640px]"
        preserveAspectRatio="xMidYMid meet"
        style={{ overflow: 'visible' }}
      >
        {/* Drifting hand-drawn baseline */}
        <motion.path
          d={baselinePath}
          fill="none"
          stroke={`rgba(${strokeRGB}, 0.5)`}
          strokeWidth="2.2"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={inView ? { pathLength: 1 } : {}}
          transition={{ duration: 1 }}
        />

        {/* Era bars */}
        {ERAS.map((era, i) => (
          <HandDrawnEra
            key={era.year}
            era={era}
            x={START_X + i * BAR_GAP}
            index={i}
            delay={0.25 + i * 0.18}
            inView={inView}
            strokeRGB={strokeRGB}
          />
        ))}
      </svg>
    </div>
  );
}
